import { createServerFn } from "@tanstack/react-start";
import { setResponseHeader } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export type CommunityPageData = {
  counts: { people: number; circles: number; experts: number; events_ahead: number };
  expertCategories: { category: string; n: number }[];
  circles: { id: string; name: string; topics: string[] }[];
};

/**
 * Данные для публичной страницы /community: только агрегаты (platform_counts,
 * public_community_expert_categories) и список сообществ из id/name/topics.
 * Строки профилей и контакты не отдаются.
 */
export const getCommunityPageData = createServerFn({ method: "GET" }).handler(
  async (): Promise<CommunityPageData> => {
    setResponseHeader("Cache-Control", "public, s-maxage=900, stale-while-revalidate=3600");
    const url = process.env.SUPABASE_URL ?? import.meta.env.VITE_SUPABASE_URL;
    const key = process.env.SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const sb = createClient<Database>(url, key, {
      auth: { persistSession: false, autoRefreshToken: false },
    });
    const [countsRes, catsRes, circlesRes] = await Promise.all([
      sb.rpc("platform_counts" as never),
      sb.rpc("public_community_expert_categories" as never),
      sb.rpc("public_community_circles" as never),
    ]);
    if (countsRes.error || catsRes.error || circlesRes.error) throw new Error("Community data unavailable");
    const c = (countsRes.data ?? {}) as Record<string, unknown>;
    const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
    return {
      counts: {
        people: num(c.people),
        circles: num(c.circles),
        experts: num(c.experts),
        events_ahead: num(c.events_ahead),
      },
      expertCategories: ((catsRes.data ?? []) as { category: string; n: number }[]).map((r) => ({
        category: String(r.category),
        n: num(r.n),
      })),
      circles: ((circlesRes.data ?? []) as { id: string; name: string; topics: string[] | null }[]).map((r) => ({
        id: String(r.id),
        name: String(r.name),
        topics: Array.isArray(r.topics) ? r.topics.map(String) : [],
      })),
    };
  },
);
